import React from 'react';
import { TrendingUp, MapPin, Home, ExternalLink, CalendarClock, ClipboardList, Ruler } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { createAppointment } from './crmApi';

function fmtRange(low, high) {
  const clean = (v) => {
    if (!v) return '?';
    const s = String(v).replace(/[^0-9]/g, '');
    if (!s) return String(v);
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString('en-US') : String(v);
  };
  return `$${clean(low)} – $${clean(high)}`;
}

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Small helper — build a Date 3 business days out at 10 AM local.
function suggestSlotIso() {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  d.setHours(10, 0, 0, 0);
  return d.toISOString();
}

export default function ValuationDetailDialog({ item, open, onOpenChange }) {
  const { toast } = useToast();
  const v = item?.valuation || {};

  const bookWalkthrough = async () => {
    try {
      await createAppointment({
        title: 'Home valuation walkthrough',
        address: v.address || '',
        client_name: item.contact || '',
        client_phone: item.number || '',
        start: suggestSlotIso(),
        duration_min: 30,
        notes: `Follow-up on AI valuation (${fmtRange(v.avm_low, v.avm_high)}). Spot interior upgrades algorithms missed.`,
      });
      toast({ title: 'Walkthrough drafted',
              description: '30-min slot penciled for 3 business days out. Edit time on the Appointments page.' });
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Draft failed',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    }
  };

  const orderAppraisal = () => {
    // Deep-link to the Referrals page filtered to appraisers.
    onOpenChange(false);
    window.location.assign('/crm/settings/professionals?category=appraiser');
  };

  const buildCma = async () => {
    try {
      await createAppointment({
        title: 'Build CMA report',
        address: v.address || '',
        client_name: item.contact || '',
        client_phone: item.number || '',
        start: suggestSlotIso(),
        duration_min: 60,
        notes: `Custom CMA request from AI valuation call (${fmtRange(v.avm_low, v.avm_high)}). Track pending bidding wars in the area.`,
      });
      toast({ title: 'CMA prep task drafted',
              description: '60-min block penciled. Adjust time on the Appointments page.' });
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Draft failed',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="valuation-detail-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-yellow-700" /> AI valuation summary
          </DialogTitle>
          <DialogDescription>
            The caller heard this range live. Pick the next step below to move them from a number to a signed engagement.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4 space-y-2">
          <div className="text-xs uppercase tracking-wide text-yellow-800 font-semibold">Preliminary AVM range</div>
          <div className="text-2xl font-serif text-brand-green" data-testid="valuation-range">
            {fmtRange(v.avm_low, v.avm_high)}
          </div>
          {v.address && (
            <div className="text-sm text-brand-green/80 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {v.address}
            </div>
          )}
        </div>

        <dl className="text-sm space-y-2 mt-3">
          <div className="flex gap-2"><dt className="text-brand-green/60 w-28 shrink-0">Caller</dt>
            <dd className="text-brand-green">{item.contact || '—'} · {item.number || '—'}</dd></div>
          <div className="flex gap-2"><dt className="text-brand-green/60 w-28 shrink-0">Received</dt>
            <dd className="text-brand-green">{fmt(item.at)}</dd></div>
          {v.condition && (
            <div className="flex gap-2"><dt className="text-brand-green/60 w-28 shrink-0">Condition</dt>
              <dd className="text-brand-green">{v.condition}</dd></div>
          )}
          {v.upgrades && (
            <div className="flex gap-2"><dt className="text-brand-green/60 w-28 shrink-0">Upgrades</dt>
              <dd className="text-brand-green">{v.upgrades}</dd></div>
          )}
        </dl>

        <div className="mt-4 border-t border-brand-cream pt-3">
          <div className="text-xs uppercase tracking-wide text-brand-green/60 mb-2">Next step</div>
          <div className="grid grid-cols-1 gap-2">
            <Button variant="outline" onClick={bookWalkthrough}
                    data-testid="valuation-book-walkthrough"
                    className="justify-start gap-2 h-auto py-3">
              <CalendarClock className="w-4 h-4 text-brand-green shrink-0" />
              <div className="text-left">
                <div className="font-semibold text-brand-green">Book 15-min walkthrough</div>
                <div className="text-xs text-brand-green/60">Spot interior upgrades the algorithm can't see.</div>
              </div>
            </Button>
            <Button variant="outline" onClick={orderAppraisal}
                    data-testid="valuation-order-appraisal"
                    className="justify-start gap-2 h-auto py-3">
              <Ruler className="w-4 h-4 text-brand-green shrink-0" />
              <div className="text-left">
                <div className="font-semibold text-brand-green">Order formal appraisal</div>
                <div className="text-xs text-brand-green/60">Jump to your appraiser rolodex for a legally binding valuation.</div>
              </div>
              <ExternalLink className="w-3 h-3 ml-auto text-brand-green/50" />
            </Button>
            <Button variant="outline" onClick={buildCma}
                    data-testid="valuation-build-cma"
                    className="justify-start gap-2 h-auto py-3">
              <ClipboardList className="w-4 h-4 text-brand-green shrink-0" />
              <div className="text-left">
                <div className="font-semibold text-brand-green">Build custom CMA</div>
                <div className="text-xs text-brand-green/60">Comparative analysis tracking pending neighborhood bidding wars.</div>
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
